import * as jiff from 'jiff';
import * as moment from 'moment';

// tslint:disable: max-line-length
import { ApplicationRef, Injectable } from '@angular/core';
import { DocumentModelFactoryService, DocumentModelType, DocumentModelValidationService, DocumentRestService, DocumentSyncService, ExerciseProgramDocument, PlanDocument, PlanDocumentCache, PlanGroup, PlanMetadata, PlannedTier, PlanUser, Template, UserLibrary, UserLibraryDocument, Workout } from 'fitforce-document-sync';
import { cloneDeep, difference } from 'lodash';
import { BehaviorSubject, concat, forkJoin, interval, Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, first, map, tap } from 'rxjs/operators';

import { MatSnackBar } from '@angular/material/snack-bar';
import { User } from 'app/classes/user';
import { AuthService } from 'app/services/auth.service';
import { OfflineService } from 'app/services/offline.service';
import { SyncLoggingService } from 'fitforce-logging';
import { ObserverService } from 'fitforce-offline-first';
import { BackgroudSyncService } from '../../services/backgroud-sync.service';

// tslint:enable: max-line-length

interface AppDocumentStore {
  exerciseProgram: ExerciseProgramDocument;
  currentPlanDocument: PlanDocument;
  planMetadata: PlanMetadata[];
  undeletedPlanMetadata: PlanMetadata[];
  currentUser: User;
  userLibrary: UserLibrary;
}

export enum SyncStatus {
  Syncing,
  Synced,
  HasChanges,
}

@Injectable({
  providedIn: 'root',
})
export class DocumentService extends DocumentSyncService {
  private syncStatusSubject: BehaviorSubject<SyncStatus>;
  store: AppDocumentStore;

  get syncStatus() {
    return this.syncStatusSubject.asObservable();
  }

  constructor(
    private backgroundSyncService: BackgroudSyncService,
    private authService: AuthService,
    documentRestService: DocumentRestService,
    offline: OfflineService,
    syncLog: SyncLoggingService,
    private appRef: ApplicationRef,
    private documentValidationService: DocumentModelValidationService,
    private snackBar: MatSnackBar,
    private factory: DocumentModelFactoryService,
    protected observerService: ObserverService,
  ) {
    super(documentRestService, offline, syncLog, observerService);
    this.syncKey = 'documentService';
    this.store = {
      exerciseProgram: undefined,
      currentPlanDocument: undefined,
      planMetadata: [],
      undeletedPlanMetadata: [],
      currentUser: undefined,
      userLibrary: undefined,
    };
    this.syncStatusSubject = new BehaviorSubject(SyncStatus.Synced);

    concat(this.appRef.isStable.pipe(first(isStable => isStable === true)), interval(200)).pipe(
      map(() => {
        if (this.backgroundSyncService.syncing) {
          this.syncStatusSubject.next(SyncStatus.Syncing);
        } else if (this.store.planMetadata.filter(record => record.isDownloaded).some(record => record.isDirty)) {
          this.syncStatusSubject.next(SyncStatus.HasChanges);
        } else if (SyncStatus.Synced) {
          this.syncStatusSubject.next(SyncStatus.Synced);
        }
      }),
    ).subscribe();
  }

  /**
   * Returns true if service state has enough data, and false otherwise
   */
  protected hasData(): Observable<boolean> {
    return of(this.store.exerciseProgram && this.store.planMetadata && this.store.currentUser ? true : false);
  }

  setCurrentPlan(uuid: string): Observable<void> {
    let planOwnerUUID: string;
    return this.offline.getRecord<PlanMetadata>(PlanMetadata, uuid).pipe(
      concatMap(planMetadata => {
        if (planMetadata) {
          planOwnerUUID = planMetadata.userUUID;

          let obs = of(null);
          if (!planMetadata.isDownloaded) {
            obs = this.pullPlans([uuid]);
          }

          return obs.pipe(
            concatMap(() => this.offline.getRecord<PlanDocument>(PlanDocument, uuid)),
            tap(plan => {
              this.store.currentPlanDocument = plan;
              this.store.currentPlanDocument.userUUID = planOwnerUUID;
            }),
          );
        } else {
          return of(null);
        }
      }),
      map(() => null),
    );
  }

  /**
   * Syncs the plan with the server
   * @param uuid plan document uuid
   */
  syncPlan(uuid: string): Observable<void> {
    this.syncProgressSubject.next(0);

    this.syncsDone = 0;
    this.syncsTotal = 3;

    this.syncLog.info('Saving plan document changes to the server.');
    return this.pushPlan(uuid).pipe(
      concatMap(() => {
        this.syncLog.info('Fetching plan document from the server and saving to offline database.');
        return this.pullPlans([uuid], true);
      }),
      concatMap(() => this.markDownloaded(uuid)),
      concatMap(() => {
        this.syncLog.info('Restoring cached plan from offline database.');
        return this.restorePlanMetadata();
      }),
      tap(() => {
        this.updateTotalProgress(1);
      }),
    );
  }

  /**
   * Saves a plan metadata record to the offline database.
   * @param planMetadata plan metadata record to save to offline database
   */
  createPlan(planMetadata: PlanMetadata): Observable<void> {
    const plan = new PlanDocument({
      key: planMetadata.uuid,
      version: planMetadata.version || 1,
      document: {
        id: planMetadata.uuid,
        exercise_program_id: this.store.exerciseProgram.uuid,
        exercise_program_version: this.store.exerciseProgram.version,
        exercises: [],
        name: planMetadata.name,
        notes: planMetadata.notes || '',
        num_weeks: planMetadata.numWeeks,
        phases: [],
        type: 'Plan',
        version: planMetadata.version || 1,
        workouts: [],
        start_date: moment(planMetadata.startDate).format('MM/DD/YYYY'),
      },
    });

    plan.userUUID = planMetadata.userUUID;

    plan.createdAt = moment();
    planMetadata.createdAt = moment();
    planMetadata.isDownloaded = true;

    if (this.documentValidationService.validate(plan.document)) {
      return forkJoin(this.offline.save(PlanMetadata, planMetadata), this.offline.save(PlanDocument, plan)).pipe(
        concatMap(() => this.restorePlanMetadata()),
        tap(() => {
          this.backgroundSyncService.addSyncRequest(() => {
            return this.pushPlan(planMetadata.uuid).pipe(
              concatMap(() => this.pullPlans([planMetadata.uuid])),
              concatMap(() => this.restorePlanMetadata()),
            );
          });
        }),
        map(() => null),
      );
    } else {
      this.snackBar.open('Invalid document. Please correct any errors and try again.', 'Dismiss');
      const err = new Error('Validation error: Create Plan');
      return throwError(err);
    }
  }

  updatePlan(plan: PlanMetadata | PlanDocument): Observable<void> {
    return forkJoin(
      this.offline.getRecord<PlanDocument>(PlanDocument, plan.uuid),
      this.offline.getRecord<PlanMetadata>(PlanMetadata, plan.uuid),
    ).pipe(
      concatMap(result => {
        const planDocument = result[0];
        const planMetadata = result[1];
        planDocument.markDirty();
        planMetadata.markDirty();

        if (plan instanceof PlanMetadata) {
          planDocument.document.name = plan.name;
          if (planDocument.document.notes || plan.notes) {
            planDocument.document.notes = plan.notes;
          }

          planDocument.document.num_weeks = plan.numWeeks;
          planDocument.document.start_date = plan.startDate.format('MM/DD/YYYY');

          planMetadata.name = plan.name;
          planMetadata.notes = plan.notes;
          planMetadata.numWeeks = plan.numWeeks;
          planMetadata.startDate = plan.startDate;

          // Remove phases beyond num weeks provided
          planDocument.document.phases = planDocument.document.phases.filter((p, i, a) => {
            return (+p.end_week) < plan.numWeeks;
          });

          // Remove workouts beyond num weeks provided
          planDocument.document.workouts = planDocument.document.workouts.filter((w, i, a) => {
            return w.schedule.week[0] < plan.numWeeks;
          });
        } else if (plan instanceof PlanDocument) {
          planDocument.document = plan.document;

          planMetadata.name = plan.document.name;
          planMetadata.startDate = moment(plan.document.start_date);
          planMetadata.numWeeks = plan.document.num_weeks;
          planMetadata.notes = plan.document.notes;
        }
        if (this.documentValidationService.validate(planDocument.document)) {
          return forkJoin(this.offline.save(PlanMetadata, planMetadata), this.offline.save(PlanDocument, planDocument));
        } else {
          this.snackBar.open('Invalid document. Rolling back last change.', 'Dismiss');

          return this.restoreData();
        }
      }),
      concatMap(() => this.restorePlanMetadata()),
      tap(() => {
        this.backgroundSyncService.addSyncRequest(() =>
          this.push().pipe(
            catchError(error => {
              if (error && error.status === 400) {
                // roll back to latest version on the server
                this.snackBar.open('Invalid patch. Rolling back to last synced.', 'Dismiss');

                return this.documentRestService.getPlanDocument(plan.uuid).pipe(
                  concatMap(remoteDocument => {
                    return forkJoin(
                      this.offline.save(PlanDocument, remoteDocument),
                      this.offline.save(PlanDocumentCache, new PlanDocumentCache(remoteDocument)),
                    );
                  }),
                  concatMap(() => this.restorePlanMetadata()),
                  concatMap(() => {
                    if (this.store.currentPlanDocument) {
                      return this.setCurrentPlan(plan.uuid);
                    } else {
                      return of(null);
                    }
                  }),
                );
              } else {
                throw error;
              }
            }),
            concatMap(() => this.restorePlanMetadata()),
          ),
        );
      }),
    );
  }

  /**
   * Copies a plan document to another user's library.
   * @param planMetadata plan metadata record to copy
   * @param userUUID user uuid to copy plan to
   * @param override object with metadata keys to override
   */
  copyPlan(planMetadata: PlanMetadata, userUUID: string, override?: Object): Observable<void> {
    let pullPlanObs = of(null);
    if (!planMetadata.isDownloaded) {
      pullPlanObs = this.pullPlans([planMetadata.uuid]).pipe(concatMap(() => this.markDownloaded(planMetadata.uuid)));
    }

    const copiedPlanMetadata: PlanMetadata = cloneDeep<PlanMetadata>(planMetadata);
    copiedPlanMetadata.generateId();
    copiedPlanMetadata.markCreated();
    copiedPlanMetadata.id = copiedPlanMetadata.uuid;
    copiedPlanMetadata.userUUID = userUUID;
    copiedPlanMetadata.version = 1;
    copiedPlanMetadata.sharedWithUsers = [];
    copiedPlanMetadata.createdByUser = {
      email: this.store.currentUser.email,
      firstName: this.store.currentUser.firstName,
      lastName: this.store.currentUser.lastName,
      uuid: this.store.currentUser.uuid,
    };
    copiedPlanMetadata.isDownloaded = true;
    copiedPlanMetadata.name = `Copy of ${copiedPlanMetadata.name}`;

    // Overrides metadata keys
    if (override !== undefined) {
      Object.keys(override).forEach(key => {
        if (override[key] !== null) {
          copiedPlanMetadata[key] = override[key];
        }
      });
    }

    return pullPlanObs.pipe(
      concatMap(() => this.offline.getRecord<PlanDocument>(PlanDocument, planMetadata.uuid)),
      concatMap(plan => {
        const copiedPlan: PlanDocument = cloneDeep<PlanDocument>(plan);
        copiedPlan.markCreated();
        copiedPlan.uuid = copiedPlanMetadata.uuid;
        copiedPlan.userUUID = userUUID;
        copiedPlan.version = 1;
        copiedPlan.document.name = copiedPlanMetadata.name;
        copiedPlan.document.id = copiedPlanMetadata.id;
        copiedPlan.document.start_date = copiedPlanMetadata.startDate.format('MM/DD/YYYY');
        copiedPlan.document.num_weeks = copiedPlanMetadata.numWeeks;
        copiedPlan.document.notes = copiedPlanMetadata.notes;

        if (this.documentValidationService.validate(copiedPlan.document)) {
          return forkJoin(this.offline.save(PlanMetadata, copiedPlanMetadata), this.offline.save(PlanDocument, copiedPlan));
        } else {
          this.snackBar.open('Invalid document. Rolling back copy.', 'Dismiss');

          return this.restorePlanMetadata();
        }
      }),
      concatMap(() => this.restorePlanMetadata()),
      tap(() => this.backgroundSyncService.addSyncRequest(() => this.push().pipe(concatMap(() => this.restorePlanMetadata())))),
    );
  }

  /**
   * Delets a plan document from the offline database.
   * @param uuid plan document uuid
   */
  deletePlan(uuid: string): Observable<void> {
    return this.offline.getRecord<PlanMetadata>(PlanMetadata, uuid).pipe(
      concatMap(planMetadata => {
        if (planMetadata.isDownloaded) {
          return this.offline.getRecord<PlanDocument>(PlanDocument, uuid).pipe(
            concatMap(plan => forkJoin(this.deleteRecord(PlanMetadata, planMetadata), this.deleteRecord(PlanDocument, plan))),
            map(() => null),
          );
        } else {
          return this.deleteRecord(PlanMetadata, planMetadata);
        }
      }),
      concatMap(() => this.restorePlanMetadata()),
      tap(() => {
        if (this.store.planMetadata.find(plan => plan.uuid === uuid)) {
          this.backgroundSyncService.addSyncRequest(() => {
            return this.push().pipe(concatMap(() => this.restorePlanMetadata()));
          });
        }
      }),
    );
  }

  /**
   * Shares a plan document (read-only access) with users.
   * @param uuid plan document uuid to share
   * @param userUUIDs list of user uuids to share plan document with
   */
  sharePlan(uuid: string, userUUIDs: string[]) {
    return this.distributePlan<PlanUser>(uuid, userUUIDs, {
      addRequest: this.documentRestService.sharePlan.bind(this.documentRestService),
      removeRequest: this.documentRestService.unsharePlan.bind(this.documentRestService),
      restoreRequest: this.documentRestService.getPlanSharedUsers.bind(this.documentRestService),
      planProperty: 'sharedWithUsers',
    });
  }

  /**
   * Publishes a plan document (read-only access) to groups.
   * @param uuid plan document uuid to share
   * @param groupUUIDs list of group uuids to publish plan document to
   */
  publishPlan(uuid: string, groupUUIDs: string[]): Observable<void> {
    return this.distributePlan<PlanGroup>(uuid, groupUUIDs, {
      addRequest: this.documentRestService.publishPlan.bind(this.documentRestService),
      removeRequest: this.documentRestService.unpublishPlan.bind(this.documentRestService),
      restoreRequest: this.documentRestService.getPlanPublishedGroups.bind(this.documentRestService),
      planProperty: 'publishedToGroups',
    });
  }

  /**
   * Restores offline database to service state.
   */
  restoreData(): Observable<any> {
    return forkJoin(this.restoreCurrentUser(), this.restoreExerciseProgram(), this.restorePlanMetadata()).pipe(
      concatMap(() => this.restoreUserLibrary()),
    );
  }

  /**
   * Restores exercise program document to service state.
   */
  private restoreExerciseProgram(): Observable<void> {
    return this.offline.getAll<ExerciseProgramDocument>(ExerciseProgramDocument).pipe(
      map(exercisePrograms => {
        this.store.exerciseProgram = exercisePrograms || exercisePrograms.length > 0 ? exercisePrograms[0] : null;

        return null;
      }),
    );
  }

  /**
   * Restores plan metadata to service state.
   */
  private restorePlanMetadata(): Observable<void> {
    return this.offline.getAll<PlanMetadata>(PlanMetadata).pipe(
      map(planMetadata => {
        this.store.planMetadata = planMetadata;
        this.store.undeletedPlanMetadata = planMetadata.filter(p => !p.isDeleted);
        return null;
      }),
    );
  }

  /**
   * Restores current user information to the service state.
   */
  private restoreCurrentUser(): Observable<void> {
    return this.authService.checkForLogin().pipe(
      map(user => {
        this.store.currentUser = user;
        return null;
      }),
    );
  }

  /**
   * Restores user library to the service state
   */
  private restoreUserLibrary(): Observable<void> {
    return this.offline.getAll<UserLibraryDocument>(UserLibraryDocument).pipe(
      concatMap(lib => {
        if (lib && lib.length > 0) {
          const userLib = lib.find(l => l.document && l.document.user && l.document.user.uuid === this.store.currentUser.uuid);
          this.store.userLibrary = userLib ? userLib.document : undefined;
        }

        return of(null);
      }),
    );
  }

  private initUserLib(): void {
    const newLib = this.factory.build<UserLibrary>(DocumentModelType.UserLibrary);
    newLib.exercise_program_id = this.store.exerciseProgram.document.id;
    newLib.exercise_program_version = this.store.exerciseProgram.document.version;
    newLib.version = 1;
    newLib.user = {
      email: this.store.currentUser.email,
      uuid: this.store.currentUser.uuid,
      first_name: this.store.currentUser.firstName,
      last_name: this.store.currentUser.lastName,
      rank: this.store.currentUser.rank,
    };
    this.store.userLibrary = newLib;
  }

  /**
   * Adds a workout template
   * @param name - name of workout template (maybe different for workout name)
   * @param workout - template of workout
   */
  addWorkoutTemplate(name: string, workout: Workout): Observable<void> {
    if (!this.store.userLibrary) {
      this.initUserLib();
    }

    // add workout as Template to user library
    const lib = cloneDeep(this.store.userLibrary);
    const template = this.factory.build<Template>(DocumentModelType.Template);
    template.name = name;
    template.template = workout;
    lib.workouts.push(template);
    // save offline
    return this.saveUserLibrary(lib).pipe(
      concatMap(() => this.restoreUserLibrary()),
      // add sync request
      tap(() => this.backgroundSyncService.addSyncRequest(() =>
        this.pushUserLibary().pipe(
          concatMap(() => this.restoreUserLibrary()),
        )
      )),
    );
  }

  /**
   * Adds a tier template
   * @param name - name of tier template (maybe different for tier name)
   * @param tier - template of tier
   */
  addTierTemplate(name: string, tier: PlannedTier): Observable<void> {
    if (!this.store.userLibrary) {
      this.initUserLib();
    }

    // add tier as Template to user library
    const lib = cloneDeep(this.store.userLibrary);
    const template = this.factory.build<Template>(DocumentModelType.Template);
    template.name = name;
    template.template = tier;
    lib.tiers.push(template);
    return this.saveUserLibrary(lib).pipe(
      concatMap(() => this.restoreUserLibrary()),
      // add sync request
      tap(() => this.backgroundSyncService.addSyncRequest(() =>
        this.pushUserLibary().pipe(
          concatMap(() => this.restoreUserLibrary()),
        )
      )),
    );
  }

  /**
   * Removes template from user library
   * @param {Template} Template - template to remove
   */
  removeTemplate(template: Template): Observable<void> {
    if (!this.store.userLibrary) {
      this.initUserLib();
      return of(null);
    } else {
      // Copy user library
      const lib: UserLibrary = cloneDeep(this.store.userLibrary);
      // Remove the template from library
      lib.tiers = this.store.userLibrary.tiers.filter((tier) => {
        return tier.id !== template.id;
      });

      lib.rows = this.store.userLibrary.rows.filter((row) => {
        return row.id !== template.id;
      });

      lib.workouts =  this.store.userLibrary.workouts.filter((workout) => {
        return workout.id !== template.id;
      });

      return this.saveUserLibrary(lib).pipe(
        concatMap(() => this.restoreUserLibrary()),
        // add sync request
        tap(() => this.backgroundSyncService.addSyncRequest(() =>
          this.pushUserLibary().pipe(
            concatMap(() => this.restoreUserLibrary()),
          )
        )),
      );
    }
  }

  compareWorkouts(a: Workout, b: Workout) {
    const patch = jiff.diff(a, b);
    return patch.filter(p => p.path !== '/complete'
      && p.path !== '/est_rpe'
      && p.path !== '/est_completion_time_ms'
      && p.path !== '/name'
    ).length > 0;
  }

  private saveUserLibrary(userLibrary: UserLibrary): Observable<void> {
    return this.offline.getRecord<UserLibraryDocument>(UserLibraryDocument, this.store.userLibrary.id).pipe(
      concatMap(record => {
        if (record) {
          record.document = userLibrary;
          if (!record.createdAt) {
            record.markUpdated();
          }
        } else {
          record = new UserLibraryDocument({
            uuid: userLibrary.id,
            document: userLibrary,
            version: 1
          });
          record.createdAt = moment();
        }
        return this.offline.save(UserLibraryDocument, record);
      }),
      map(() => null),
    );
  }

  /**
   * Helper function to handle distributing a plan to groups or users
   * @param uuid plan uuid
   * @param distributedUUIDs array of group or user uuids to distribute the plan to
   * @param options.addRequest request to add a distribution
   * @param options.removeRequest request to remove a distribution
   * @param options.restoreRequest request to restore the distribution state to the plan
   * @param options.planProperty plan property name containing distribution state
   */
  private distributePlan<T extends PlanUser | PlanGroup>(
    uuid: string,
    distributedUUIDs: string[],
    options: {
      addRequest: (uuid: string, distributedUUIDs: string[]) => Observable<void>;
      removeRequest: (uuid: string, distributedUUIDs: string[]) => Observable<void>;
      restoreRequest: (uuid: string) => Observable<T[]>;
      planProperty: string;
    },
  ): Observable<void> {
    return this.offline.getRecord<PlanMetadata>(PlanMetadata, uuid).pipe(
      concatMap(planMetadata => {
        let removeObs = of(null);
        let addObs = of(null);

        const removed = difference(
          (planMetadata[options.planProperty] || []).map((record: T) => record.uuid),
          distributedUUIDs,
        );
        const added = difference(
          distributedUUIDs,
          (planMetadata[options.planProperty] || []).map((record: T) => record.uuid),
        );

        if (removed.length > 0) {
          removeObs = options.removeRequest(uuid, removed);
        }

        if (added.length > 0) {
          addObs = options.addRequest(uuid, distributedUUIDs);
        }

        return removeObs.pipe(
          concatMap(() => addObs),
          concatMap(() => options.restoreRequest(uuid)),
          concatMap((records: T[]) => {
            planMetadata[options.planProperty] = records as T[];
            return this.offline.save(PlanMetadata, planMetadata);
          }),
          concatMap(() => this.restorePlanMetadata()),
        );
      }),
    );
  }
}
